import { pluck } from '../../../store.decorator';
import { TemplateConfig } from '../../core/config.scorecard.model';
import {
    OptionalColumn,
    OptionalColumnType,
} from '../../core/definition.scorecard.model';
import {
    ColumnNameOverride,
    TemplateMode,
} from '../../core/template-definition.model';
import {
    Cell,
    CellNode,
    Column,
    Row,
    RowNode,
    Sort,
    SortOrder,
    sortOrderWithDefault,
} from '../../experimental.grid';
import {
    createEmpty,
    PillarAndMetricResult,
    ScorecardDto,
} from '../../scorecard.model';

import {
    CellContext,
    ColumnContext,
    ColumnType,
    RowContext,
    TemplateID,
} from './grid.model';
import { PerformanceGrid } from './grid.state';

// TODO consolidate
export const dedup = <T>(
    arr: T[],
    lookupKey: (value: T) => string | number,
): T[] => {
    if (!arr) {
        return arr;
    }
    const lookup = {};
    return arr.reduce((p, c) => {
        const key = lookupKey(c);
        if (!lookup[key]) {
            lookup[key] = true;
            p.push(c);
        }
        return p;
    }, []);
};

function sort<T>(arr: T[], key: string): T[] {
    const ret = arr ? [...arr] : [];
    ret.sort((a, b) => a[key] - b[key]);
    return ret;
}

function compareDef(
    cellA: Cell<CellContext>,
    cellB: Cell<CellContext>,
    rowA: Row<RowContext, CellContext>,
    rowB: Row<RowContext, CellContext>,
    def: Sort,
): number {
    try {
        const type = parseInt(def.columnId);
        const strColumns = [
            ColumnType.PILLAR,
            ColumnType.METRIC_NAME,
            ColumnType.METRIC_RANK,
            ColumnType.POINTS_BAND,
            ColumnType.N_TILE_BAND,
            ColumnType.ACTUAL_TYPE,
            ColumnType.TARGET_TYPE,
            ColumnType.PERCENT_TYPE,
            ColumnType.SCORING_TYPE,
        ];
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const mode: 'number' | 'string' =
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            strColumns.indexOf(type) !== -1 ? 'string' : 'number';
        const ctxA: CellContext = cellA.ctx;
        const ctxB: CellContext = cellB.ctx;
        let valA: any;
        let valB: any;
        switch (type) {
            case ColumnType.PILLAR:
                valA =
                    ctxA.pillar &&
                    (ctxA.pillar.name ||
                        ctxA.pillar.shortName ||
                        ctxA.pillar.initials);
                valB =
                    ctxB.pillar &&
                    (ctxB.pillar.name ||
                        ctxB.pillar.shortName ||
                        ctxB.pillar.initials);
                break;
            case ColumnType.METRIC_NAME:
                valA =
                    ctxA.data &&
                    (ctxA.data.metricName ||
                        ctxA.data.metricShortName ||
                        ctxA.data.metricInitials);
                valB =
                    ctxB.data &&
                    (ctxB.data.metricName ||
                        ctxB.data.metricShortName ||
                        ctxB.data.metricInitials);
                break;
            case ColumnType.ACTUAL:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.actual;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.actual;
                break;
            case ColumnType.TARGET:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.target;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.target;
                break;
            case ColumnType.PERCENT_TO_TARGET:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.targetPercent;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.targetPercent;
                break;
            case ColumnType.WEIGHT:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.weight;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.weight;
                break;
            case ColumnType.ORIGINAL_POINTS:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.originalPoints;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.originalPoints;
                break;
            case ColumnType.ADJUSTED_POINTS:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.adjustedPoints;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.adjustedPoints;
                break;
            case ColumnType.METRIC_SCORE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.metricScore;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.metricScore;
                break;
            case ColumnType.METRIC_RANK:
                // TODO revise. backend should return rankType in order to determine which ranking to use
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.metricRankSg;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.metricRankSg;
                break;
            case ColumnType.POINTS_BAND:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.pointsBand;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.pointsBand;
                break;
            case ColumnType.N_TILE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.nTile;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.nTile;
                break;
            case ColumnType.N_TILE_BAND:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.nTileBand;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.nTileBand;
                break;
            case ColumnType.ACTUAL_TYPE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.actualType;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.actualType;
                break;
            case ColumnType.TARGET_TYPE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.targetType;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.targetType;
                break;
            case ColumnType.PERCENT_TYPE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.percentType;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.percentType;
                break;
            case ColumnType.SCORING_TYPE:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA = ctxA.data && ctxA.data.scoreType;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB = ctxB.data && ctxB.data.scoreType;
                break;
            case ColumnType.ACTUAL_TREND:
                /* null or 0 is null */
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    ctxA.data && ctxA.data.actualTrend
                        ? ctxA.data.actualTrend
                        : null;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    ctxB.data && ctxB.data.actualTrend
                        ? ctxB.data.actualTrend
                        : null;
                break;
            case ColumnType.PERCENT_TO_TARGET_TREND:
                /* null or 0 is null */
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valA =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    ctxA.data && ctxA.data.targetPercentTrend
                        ? ctxA.data.targetPercentTrend
                        : null;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                valB =
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    ctxB.data && ctxB.data.targetPercentTrend
                        ? ctxB.data.targetPercentTrend
                        : null;
                break;
            case ColumnType.COACHING_PLAN_ACTIVE:
                valA =
                    ctxA.data && ctxA.data.coachingPlanActive === true ? 1 : -1;
                valB =
                    ctxB.data && ctxB.data.coachingPlanActive === true ? 1 : -1;
                break;
            // TODO others
            default:
                console.error('invalid column');
                return Number.MIN_SAFE_INTEGER;
        }
        if (mode === 'number') {
            valA = valA == null ? Number.MIN_SAFE_INTEGER : valA;
            valB = valB == null ? Number.MIN_SAFE_INTEGER : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        return mode === 'number'
            ? def.order === SortOrder.ASC
                ? valA - valB
                : valB - valA
            : def.order === SortOrder.ASC
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}

function compare(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return compareDef(cellA.def, cellB.def, rowA.def, rowB.def, def);
}

/** adjusts rowSpan for each pillar */
export function applyPillarRowSpan(
    rows: Array<Row<RowContext, CellContext>>,
    columns: Array<Column<ColumnContext>>,
    sortDef: Sort,
): void {
    let tempRows = rows;

    // preemptively sort rows before the grid does, but only here
    if (sortDef) {
        const column = columns.find((e) => e.id === sortDef.columnId);
        tempRows = [...rows].sort((a, b) => {
            const cellA = a.cells.find((l) => l.columnId === column.id);
            const cellB = b.cells.find((l) => l.columnId === column.id);
            // compareDef() is used by compare() which is applied to all columns
            return compareDef(cellA, cellB, a, b, sortDef);
        });
    }

    let prevPillarId: string = null;
    let firstRowOfCurrentPillar: Row<RowContext, CellContext> = null;
    let rowCount: number = 0;

    tempRows.forEach((r, ri) => {
        r.cells.forEach((l) => {
            l.rowSpan = undefined;
        });

        r.ctx.isLastRowOfPillar = false; // Set isLastRowOfPillar to false initially on the row

        const pillarId = r.cells[0].ctx.pillar.identifier;

        if (prevPillarId !== pillarId) {
            // New pillar starts
            if (firstRowOfCurrentPillar) {
                // Assign the rowSpan
                firstRowOfCurrentPillar.cells[0].rowSpan = rowCount;
                // Make the last row of the previous pillar as the last row
                tempRows[ri - 1].ctx.isLastRowOfPillar = true;
            }

            // Reset rowCount for new pillar, update prevPillarId, and set the first row of the current pillar
            rowCount = 1;
            prevPillarId = pillarId;
            firstRowOfCurrentPillar = r;
        } else {
            rowCount++;
        }

        if (ri === tempRows.length - 1) {
            // If this is the last row of all, set isLastRowOfPillar to true
            r.ctx.isLastRowOfPillar = true;
            // if is the last row, assign the rowSpan
            firstRowOfCurrentPillar.cells[0].rowSpan = rowCount;
        }
    });
}

function makeColumns(
    tplConfig: TemplateConfig,
    columnNameOverrides: ColumnNameOverride[],
): Array<Column<ColumnContext>> {
    const width = 80;

    const p = pluck<OptionalColumn[]>(
        tplConfig.pillars,
        'metrics[].optionalColumns[]',
    );
    const d = dedup<OptionalColumn>(p, (e) => e.columnType);
    const s = sort(d, 'displayOrder');

    const columns: Array<Column<ColumnContext>> = [
        {
            id: ColumnType.PILLAR.toString(),
            width,
            compareFn: compare,
            sticky: 'left',
            ctx: { templateId: TemplateID.HEADER, type: ColumnType.PILLAR },
        },
        {
            id: ColumnType.METRIC_NAME.toString(),
            width,
            compareFn: compare,
            sticky: 'left',
            ctx: {
                templateId: TemplateID.HEADER,
                type: ColumnType.METRIC_NAME,
            },
        },
    ];

    if (s.length === 0) {
        s.push({ columnType: OptionalColumnType.ACTUAL, displayOrder: 1 });
    }

    s.forEach((c) => {
        const nameOverride = columnNameOverrides
            ? columnNameOverrides.find((e) => e.columnType === c.columnType)
            : null;
        columns.push({
            id: c.columnType.toString(),
            width,
            compareFn: compare,
            ctx: {
                templateId: TemplateID.HEADER,
                type: c.columnType as any as ColumnType,
                nameOverride: nameOverride ? nameOverride.name : null,
            },
        });
    });

    return columns;
}

function makeRows(
    tplConfig: TemplateConfig,
    columns: Column[],
    data: PillarAndMetricResult[],
): Array<Row<RowContext>> {
    const height = 30;
    const rows: Array<Row<RowContext>> = [];

    const pillars = sort(tplConfig.pillars, 'displayOrder');
    pillars.forEach((p) => {
        const metrics = sort(p.metrics, 'displayOrder');
        metrics.forEach((m) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const dat =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                data &&
                data.find(
                    (e) =>
                        e.pillarId.toString() === p.identifier &&
                        e.metricId === m.metricId,
                );
            if (!dat) {
                return;
            }

            const cells: Array<Cell<CellContext>> = columns.map((c) => ({
                columnId: c.id,
                height,
                ctx: {
                    templateId: TemplateID.CONTENT,
                    pillar: p,
                    metric: m,
                    data: dat,
                },
            }));

            rows.push({
                id: `${p.identifier}-${m.metricId}`,
                height,
                ctx: {},
                cells,
            });
        });
    });

    // TODO canExpandMetrics / metric child user rows

    return rows;
}

export function syncPerformanceGrid(
    tplConfig: TemplateConfig,
    columnNameOverrides: ColumnNameOverride[],
    header: ScorecardDto,
    data: PillarAndMetricResult[],
    sortDefs: Sort[],
): PerformanceGrid {
    header = header || createEmpty();
    const nextColumns = makeColumns(tplConfig, columnNameOverrides);
    const nextRows = makeRows(tplConfig, nextColumns, data);

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const sortDef = sortDefs && sortDefs.length ? sortDefs[0] : null;
    applyPillarRowSpan(nextRows, nextColumns, sortDef);

    return {
        superGroupName: header.groupName,
        periodStartDate: header.startDate,
        periodEndDate: header.endDate,
        scorecardId: header.scorecardId,
        published: header.published,
        generatedOn: header.generatedOn,
        canExpandMetrics: tplConfig.mode === TemplateMode.MANAGER,
        columns: nextColumns,
        rows: nextRows,
        sortDefs,
    };
}

export function sortPerformanceGrid(
    columnId: string,
    columns: Array<Column<ColumnContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
): { rows: Array<Row<RowContext>>; sortDefs: Sort[] } {
    const column = columns.find((e) => e.id === columnId);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const sortDef = sortDefs && sortDefs.length ? sortDefs[0] : null;
    const defaultSortDef = {
        columnId: ColumnType.PILLAR.toString(),
        order: SortOrder.ASC,
    };
    const nextSortDefs = sortOrderWithDefault(
        columns,
        column,
        sortDef,
        defaultSortDef,
    );

    applyPillarRowSpan(rows, columns, nextSortDefs[0]);

    return { rows: [...rows], sortDefs: nextSortDefs };
}
